import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { notification, Tooltip, Switch } from 'antd';
import {
  PlayCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SoundOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { formatDateTime } from '../../utils/helpers';
import { ThemeButton, ThemeFilterBtn, ThemeTable } from '../Styles/Theme';
import { HeaderInfoCard } from './CampaignDetailStyle';
import { ThemeProjectTabWrapper } from '../Projects/ProjectsStyle';
import {
  DurationDisplay,
  ParticipantIdCell,
  HeaderActionsWrapper,
} from './CampaignCallsStyle';
import { CallStatusTag, TranscriptStatusTag } from '../../Components/StatusTag';
import TranscriptViewer from './TranscriptViewer';
import AudioPlayer from './AudioPlayer';
import {
  getCampaignCallsApi,
  getCampaignCallsReset,
  processCampaignTranscriptsApi,
  processCampaignTranscriptsReset,
  retryFailedTranscriptsApi,
  retryFailedTranscriptsReset,
  updateCallQualityApi,
  updateCallQualityReset,
  refreshCallItemApi,
  refreshCallItemReset,
  reprocessCallApi,
  reprocessCallReset,
  bulkReprocessCallsApi,
  bulkReprocessCallsReset,
  toggleCallStarApi,
  toggleCallStarReset,
} from '../../store/actions/campaignAction';
import { useCampaignPermissions, isSuperAdmin } from '../../utils/permissions';

// Helper to format call duration
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const CampaignCallsSection = (props) => {
  const {
    campaignId,
    onQualityChange,
    // Redux state
    getCampaignCalls,
    getCampaignCallsReset,
    getCampaignCallsState,
    processCampaignTranscripts,
    processCampaignTranscriptsReset,
    processCampaignTranscriptsState,
    retryFailedTranscripts,
    retryFailedTranscriptsReset,
    retryFailedTranscriptsState,
    updateCallQuality,
    updateCallQualityReset,
    updateCallQualityState,
    refreshCallItem,
    refreshCallItemReset,
    refreshCallItemState,
    reprocessCall,
    reprocessCallReset,
    reprocessCallState,
    bulkReprocessCalls,
    bulkReprocessCallsReset,
    bulkReprocessCallsState,
    toggleCallStar,
    toggleCallStarReset,
    toggleCallStarState,
  } = props;

  const { can } = useCampaignPermissions();
  const canBulkReprocess = isSuperAdmin() || can('bulk-reprocess-calls');

  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [droppedCount, setDroppedCount] = useState(0);
  const [starredCount, setStarredCount] = useState(0);
  const [droppedFilter, setDroppedFilter] = useState(false);
  const [starredFilter, setStarredFilter] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [transcriptModalVisible, setTranscriptModalVisible] = useState(false);
  const [audioModalVisible, setAudioModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    sortBy: 'call_completed_at',
    sortDirection: 'desc',
  });

  // Fetch data on mount
  useEffect(() => {
    if (campaignId) {
      getCampaignCalls(campaignId, filters);
    }
    return () => {
      getCampaignCallsReset();
    };
  }, [campaignId]);

  // Fetch calls when filters, tab, or dropped/starred filter change
  useEffect(() => {
    if (campaignId) {
      const params = { ...filters };
      if (activeTab !== 'all') {
        params.quality = activeTab;
      }
      if (droppedFilter) {
        params.dropped = 'true';
      }
      if (starredFilter) {
        params.starred = 'true';
      }
      getCampaignCalls(campaignId, params);
    }
  }, [filters, activeTab, droppedFilter, starredFilter]);

  // Handle calls fetch response
  useEffect(() => {
    if (getCampaignCallsState.apiState === 'success' && getCampaignCallsState.data) {
      const callsData = getCampaignCallsState.data.data || getCampaignCallsState.data;
      const calls = callsData.calls || [];

      setTotalRecords(callsData.pagination?.total || callsData.total_count || 0);

      // Get stats from the same response
      if (callsData.stats) {
        setPendingCount(callsData.stats.transcript_pending || 0);
        setFailedCount(callsData.stats.transcript_failed || 0);
        setDroppedCount(callsData.stats.dropped_calls || 0);
        setStarredCount(callsData.stats.starred_calls || 0);
      }

      if (calls && calls.length > 0) {
        const updatedCalls = calls.map((item) => ({
          key: item._id,
          id: item._id,
          participantId: item.pin || item.participant_id || item.ps_participant_id || '-',
          participantName: item.invite_name || null,
          callDate: formatDateTime(item.call_completed_at),
          callDateRaw: item.call_completed_at,
          duration: item.call_duration_seconds || item.call_duration || 0,
          callStatus: item.call_status || 'pending',
          transcriptStatus: item.transcript_status || 'not_available',
          hasTranscript: item.transcript_status === 'uploaded',
          transcriptQuality: item.transcript_quality || 'neutral',
          audioStatus: item.audio_status || 'not_available',
          hasAudio: item.has_audio || item.audio_status === 'uploaded',
          hasConversationId: item.has_conversation_id || !!item.elevenlabs_conversation_id,
          isStarred: item.is_starred || false,
          isDroppedCall: item.is_dropped_call || false,
        }));

        setTableData(updatedCalls);
      } else {
        setTableData([]);
      }

      // Notify parent to refresh transcript stats (for Close & Create Project logic)
      // This ensures stats are updated when new calls appear or list is refreshed
      if (onQualityChange) {
        onQualityChange();
      }

      getCampaignCallsReset();
    } else if (getCampaignCallsState.apiState === 'error') {
      getCampaignCallsReset();
    }
  }, [getCampaignCallsState, getCampaignCallsReset, onQualityChange]);

  // Handle process transcripts response
  useEffect(() => {
    if (processCampaignTranscriptsState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: processCampaignTranscriptsState.message || 'Transcripts processing started',
      });
      processCampaignTranscriptsReset();
      getCampaignCalls(campaignId, filters);
    } else if (processCampaignTranscriptsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: processCampaignTranscriptsState.message || 'Failed to process transcripts',
      });
      processCampaignTranscriptsReset();
    }
  }, [processCampaignTranscriptsState, processCampaignTranscriptsReset, campaignId, filters]);

  // Handle retry failed transcripts response
  useEffect(() => {
    if (retryFailedTranscriptsState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: retryFailedTranscriptsState.message || 'Retrying failed transcripts',
      });
      retryFailedTranscriptsReset();
      getCampaignCalls(campaignId, filters);
    } else if (retryFailedTranscriptsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: retryFailedTranscriptsState.message || 'Failed to retry transcripts',
      });
      retryFailedTranscriptsReset();
    }
  }, [retryFailedTranscriptsState, retryFailedTranscriptsReset, campaignId, filters]);

  // Handle update call quality response
  useEffect(() => {
    if (updateCallQualityState.apiState === 'success') {
      // Update local state immediately
      setTableData((prev) =>
        prev.map((call) =>
          call.id === updateCallQualityState.callId
            ? { ...call, transcriptQuality: updateCallQualityState.quality }
            : call
        )
      );
      // Notify parent component to refresh transcript stats (for Close & Create Project logic)
      if (onQualityChange) {
        onQualityChange(updateCallQualityState.quality);
      }
      updateCallQualityReset();
    } else if (updateCallQualityState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateCallQualityState.message || 'Failed to update call quality',
      });
      updateCallQualityReset();
    }
  }, [updateCallQualityState, updateCallQualityReset, onQualityChange]);

  // Handle refresh call item response
  useEffect(() => {
    if (refreshCallItemState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: refreshCallItemState.message || `${refreshCallItemState.itemType} refreshed successfully`,
      });
      refreshCallItemReset();
      handleRefresh();
    } else if (refreshCallItemState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: refreshCallItemState.message || `Failed to refresh ${refreshCallItemState.itemType}`,
      });
      refreshCallItemReset();
    }
  }, [refreshCallItemState, refreshCallItemReset, campaignId, filters, activeTab, droppedFilter, getCampaignCalls]);

  // Handle reprocess call response
  useEffect(() => {
    if (reprocessCallState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: reprocessCallState.message || 'Call reprocessed successfully',
      });
      reprocessCallReset();
      handleRefresh();
    } else if (reprocessCallState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: reprocessCallState.message || 'Failed to reprocess call',
      });
      reprocessCallReset();
    }
  }, [reprocessCallState, reprocessCallReset]);

  // Handle toggle call star response
  useEffect(() => {
    if (toggleCallStarState.apiState === 'success') {
      const newStarred = toggleCallStarState.data?.is_starred;
      const newCount = newStarred ? starredCount + 1 : Math.max(0, starredCount - 1);
      setStarredCount(newCount);

      // Update selectedCall so open modals reflect the change
      setSelectedCall((prev) =>
        prev && prev.id === toggleCallStarState.callId
          ? { ...prev, isStarred: newStarred }
          : prev
      );

      // If unstarring while starred filter is active
      if (!newStarred && starredFilter) {
        toggleCallStarReset();
        // If last starred item, auto-disable filter (useEffect will re-fetch without starred param)
        if (newCount === 0) {
          setStarredFilter(false);
        } else {
          // Still have starred items, re-fetch to remove the unstarred row
          handleRefresh();
        }
        return;
      }

      // If starring while starred filter is active, re-fetch to include it in the filtered list
      if (newStarred && starredFilter) {
        toggleCallStarReset();
        handleRefresh();
        return;
      }

      setTableData((prev) =>
        prev.map((call) =>
          call.id === toggleCallStarState.callId
            ? { ...call, isStarred: newStarred }
            : call
        )
      );
      toggleCallStarReset();
    } else if (toggleCallStarState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: toggleCallStarState.message || 'Failed to update star',
      });
      toggleCallStarReset();
    }
  }, [toggleCallStarState, toggleCallStarReset]);

  // Handle bulk reprocess response
  useEffect(() => {
    if (bulkReprocessCallsState.apiState === 'success') {
      notification.success({
        message: 'Bulk Reprocess Started',
        description: bulkReprocessCallsState.data?.message || `Reprocessing ${bulkReprocessCallsState.data?.total_calls || 'all'} calls in background`,
      });
      bulkReprocessCallsReset();
    } else if (bulkReprocessCallsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: bulkReprocessCallsState.message || 'Failed to start bulk reprocess',
      });
      bulkReprocessCallsReset();
    }
  }, [bulkReprocessCallsState, bulkReprocessCallsReset]);

  const handleTableChange = (pagination, _, sorter) => {
    setFilters({
      ...filters,
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      sortBy: sorter?.field || 'call_completed_at',
      sortDirection: sorter?.order === 'ascend' ? 'asc' : 'desc',
    });
  };

  const handleViewTranscript = (record) => {
    setSelectedCall(record);
    setTranscriptModalVisible(true);
  };

  const handleViewAudio = (record) => {
    setSelectedCall(record);
    setAudioModalVisible(true);
  };

  const handleProcessTranscripts = () => {
    processCampaignTranscripts(campaignId);
  };

  const handleRetryFailed = () => {
    retryFailedTranscripts(campaignId);
  };

  const handleRefresh = () => {
    const params = { ...filters };
    if (activeTab !== 'all') {
      params.quality = activeTab;
    }
    if (droppedFilter) {
      params.dropped = 'true';
    }
    if (starredFilter) {
      params.starred = 'true';
    }
    getCampaignCalls(campaignId, params);
  };

  const handleToggleStar = (record) => {
    toggleCallStar(campaignId, record.id);
  };

  const handleToggleQuality = (record, checked) => {
    const newQuality = checked ? 'good' : 'bad';
    updateCallQuality(campaignId, record.id, newQuality);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleRefreshItem = (record, type) => {
    refreshCallItem(campaignId, record.id, type);
  };

  const handleReprocessCall = (record) => {
    reprocessCall(campaignId, record.id);
  };

  const handleBulkReprocess = () => {
    bulkReprocessCalls(campaignId);
  };

  const isLoading = getCampaignCallsState.apiState === 'loading';

  const columns = [
    {
      title: '',
      dataIndex: 'isStarred',
      key: 'isStarred',
      width: 50,
      align: 'center',
      render: (isStarred, record) => {
        const isToggling = toggleCallStarState.apiState === 'loading' && toggleCallStarState.callId === record.id;
        return (
          <span
            onClick={() => !isToggling && handleToggleStar(record)}
            style={{ cursor: isToggling ? 'default' : 'pointer', fontSize: '16px', opacity: isToggling ? 0.5 : 1 }}
          >
            {isStarred ? (
              <StarFilled style={{ color: '#FAAD14' }} />
            ) : (
              <StarOutlined style={{ color: '#d9d9d9' }} />
            )}
          </span>
        );
      },
    },
    {
      title: 'Participant ID',
      dataIndex: 'participantId',
      key: 'participantId',
      render: (text) => (
        <Tooltip title={text}>
          <ParticipantIdCell>{text}</ParticipantIdCell>
        </Tooltip>
      ),
    },
    {
      title: 'Call Date',
      dataIndex: 'callDate',
      key: 'callDate',
      sorter: true,
      width: 195,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => <DurationDisplay>{formatDuration(duration)}</DurationDisplay>,
    },
    {
      title: 'Call Status',
      dataIndex: 'callStatus',
      key: 'callStatus',
      width: 120,
      render: (status) => <CallStatusTag status={status} />,
    },
    {
      title: 'Transcript',
      dataIndex: 'transcriptStatus',
      key: 'transcriptStatus',
      width: 100,
      align: 'center',
      render: (status, record) => {
        // Show transcript icon if transcript is uploaded
        if (record.hasTranscript) {
          return (
            <Tooltip title="View Transcript">
              <ThemeFilterBtn
                shape="circle"
                size="medium"
                icon={<FileTextOutlined />}
                onClick={() => handleViewTranscript(record)}
              />
            </Tooltip>
          );
        }

        // Show refresh button if: has conversation_id, audio succeeded, transcript failed
        const canRefreshTranscript = record.hasConversationId && record.hasAudio && !record.hasTranscript;
        const isRefreshing = refreshCallItemState.apiState === 'loading'
          && refreshCallItemState.callId === record.id
          && refreshCallItemState.itemType === 'transcript';

        if (canRefreshTranscript) {
          return (
            <Tooltip title="Refresh Transcript">
              <ThemeFilterBtn
                shape="circle"
                size="medium"
                icon={<SyncOutlined spin={isRefreshing} />}
                onClick={() => handleRefreshItem(record, 'transcript')}
                loading={isRefreshing}
                disabled={isRefreshing}
              />
            </Tooltip>
          );
        }

        // Default: show status tag
        return <TranscriptStatusTag status={status} />;
      },
    },
    {
      title: 'Audio',
      dataIndex: 'audioStatus',
      key: 'audioStatus',
      width: 100,
      align: 'center',
      render: (_, record) => {
        // Show audio icon if audio is uploaded
        if (record.hasAudio) {
          return (
            <Tooltip title="Play Audio">
              <ThemeFilterBtn
                shape="circle"
                size="medium"
                icon={<SoundOutlined />}
                onClick={() => handleViewAudio(record)}
              />
            </Tooltip>
          );
        }

        // Show refresh button if: has conversation_id, transcript succeeded, audio failed
        const canRefreshAudio = record.hasConversationId && record.hasTranscript && !record.hasAudio;
        const isRefreshing = refreshCallItemState.apiState === 'loading'
          && refreshCallItemState.callId === record.id
          && refreshCallItemState.itemType === 'audio';

        if (canRefreshAudio) {
          return (
            <Tooltip title="Refresh Audio">
              <ThemeFilterBtn
                shape="circle"
                size="medium"
                icon={<SyncOutlined spin={isRefreshing} />}
                onClick={() => handleRefreshItem(record, 'audio')}
                loading={isRefreshing}
                disabled={isRefreshing}
              />
            </Tooltip>
          );
        }

        // Default: show dash
        return <span style={{ color: '#83899F' }}>-</span>;
      },
    },
    {
      title: 'Quality',
      dataIndex: 'transcriptQuality',
      key: 'transcriptQuality',
      width: 150,
      align: 'center',
      render: (quality, record) => {
        if (!record.hasTranscript) {
          return <span style={{ color: '#83899F' }}>-</span>;
        }

        const isLoading = updateCallQualityState.apiState === 'loading' && updateCallQualityState.callId === record.id;

        // If neutral (unreviewed), show greyed switch with "?"
        if (quality === 'neutral') {
          return (
            <Tooltip title="Mark this good or bad">
              <Switch
                checked={false}
                onChange={(checked) => handleToggleQuality(record, checked)}
                loading={isLoading}
                checkedChildren="?"
                unCheckedChildren="?"
                style={{
                  backgroundColor: '#d9d9d9',
                }}
              />
            </Tooltip>
          );
        }

        // If good or bad, show colored switch
        return (
          <Switch
            checked={quality === 'good'}
            onChange={(checked) => handleToggleQuality(record, checked)}
            loading={isLoading}
            checkedChildren="Good"
            unCheckedChildren="Bad"
            style={{
              backgroundColor: quality === 'good' ? '#48B569' : '#ff4d4f',
            }}
          />
        );
      },
    },
    // Action column - only show if user has reprocess-calls permission
    ...(can('reprocess-calls')
      ? [
          {
            title: 'Action',
            key: 'action',
            width: 100,
            align: 'center',
            render: (_, record) => {
              // Only show reprocess button if conversation_id exists
              if (!record.hasConversationId) {
                return <span style={{ color: '#83899F' }}>-</span>;
              }

              const isReprocessing = reprocessCallState.apiState === 'loading'
                && reprocessCallState.callId === record.id;

              return (
                <Tooltip title="Reprocess Call">
                  <ThemeFilterBtn
                    className="refresh-btn"
                    shape="circle"
                    icon={<ReloadOutlined spin={isReprocessing} />}
                    onClick={() => handleReprocessCall(record)}
                    loading={isReprocessing}
                    disabled={isReprocessing}
                  />
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <HeaderInfoCard
        title="Call Transcripts"
        className="tableCard"
        style={{ marginTop: '16px' }}
        extra={
          <HeaderActionsWrapper>
            <ThemeProjectTabWrapper>
              <div className="view-toggle">
                <button
                  className={`toggle-btn ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => handleTabChange('all')}
                >
                  All
                </button>
                <button
                  className={`toggle-btn ${activeTab === 'good' ? 'active' : ''}`}
                  onClick={() => handleTabChange('good')}
                >
                  Good
                </button>
                <button
                  className={`toggle-btn ${activeTab === 'neutral' ? 'active' : ''}`}
                  onClick={() => handleTabChange('neutral')}
                >
                  Unreviewed
                </button>
                <button
                  className={`toggle-btn ${activeTab === 'bad' ? 'active' : ''}`}
                  onClick={() => handleTabChange('bad')}
                >
                  Bad
                </button>
              </div>
            </ThemeProjectTabWrapper>
            {(starredCount > 0 || starredFilter) && (
              <button
                onClick={() => setStarredFilter(!starredFilter)}
                style={{
                  padding: '6px 14px',
                  fontSize: '10px',
                  lineHeight: '12px',
                  fontWeight: '600',
                  border: starredFilter ? 'none' : '1px solid #E0E0E0',
                  borderRadius: '10px',
                  background: starredFilter ? '#FAAD14' : '#fff',
                  color: starredFilter ? '#fff' : '#1E1E1E',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <StarFilled style={{ fontSize: '10px' }} />
                Starred ({starredCount})
              </button>
            )}
            {droppedCount > 0 && (
              <button
                onClick={() => setDroppedFilter(!droppedFilter)}
                style={{
                  padding: '6px 14px',
                  fontSize: '10px',
                  lineHeight: '12px',
                  fontWeight: '600',
                  border: droppedFilter ? 'none' : '1px solid #E0E0E0',
                  borderRadius: '10px',
                  background: droppedFilter ? '#3D2969' : '#fff',
                  color: droppedFilter ? '#fff' : '#1E1E1E',
                  cursor: 'pointer',
                }}
              >
                Dropped
              </button>
            )}
            {can('process-call-transcripts') && pendingCount > 0 && (
              <ThemeButton
                className="action-btn"
                icon={<PlayCircleOutlined />}
                onClick={handleProcessTranscripts}
                loading={processCampaignTranscriptsState.apiState === 'loading'}
              >
                Process Pending
              </ThemeButton>
            )}
            {can('process-call-transcripts') && failedCount > 0 && (
              <ThemeButton
                className="action-btn noBackground"
                icon={<SyncOutlined />}
                onClick={handleRetryFailed}
                loading={retryFailedTranscriptsState.apiState === 'loading'}
              >
                Retry Failed
              </ThemeButton>
            )}
            {canBulkReprocess && totalRecords > 0 && (
              <ThemeButton
                className="action-btn"
                icon={<ReloadOutlined />}
                onClick={handleBulkReprocess}
                loading={bulkReprocessCallsState.apiState === 'loading'}
              >
                Bulk Reprocess
              </ThemeButton>
            )}
            <Tooltip title="Refresh">
              <ThemeFilterBtn
                className="refresh-btn"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={isLoading}
              />
            </Tooltip>
          </HeaderActionsWrapper>
        }
      >
        <ThemeTable
          columns={columns}
          dataSource={tableData}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} calls`,
          }}
          onChange={handleTableChange}
          loading={isLoading}
          scroll={{ x: 768 }}
        />
      </HeaderInfoCard>

      {/* Transcript Viewer Modal */}
      <TranscriptViewer
        visible={transcriptModalVisible}
        onClose={() => {
          setTranscriptModalVisible(false);
          setSelectedCall(null);
        }}
        campaignId={campaignId}
        callId={selectedCall?.id}
        callInfo={selectedCall}
        canDownload={can('download-transcripts')}
        onStarToggle={() => selectedCall && handleToggleStar(selectedCall)}
        toggleCallStarState={toggleCallStarState}
      />

      {/* Audio Player Modal */}
      <AudioPlayer
        visible={audioModalVisible}
        onClose={() => {
          setAudioModalVisible(false);
          setSelectedCall(null);
        }}
        campaignId={campaignId}
        callId={selectedCall?.id}
        callInfo={selectedCall}
        canDownload={can('download-audio')}
        onStarToggle={() => selectedCall && handleToggleStar(selectedCall)}
        toggleCallStarState={toggleCallStarState}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  getCampaignCallsState: state.campaign.getCampaignCallsState,
  processCampaignTranscriptsState: state.campaign.processCampaignTranscriptsState,
  retryFailedTranscriptsState: state.campaign.retryFailedTranscriptsState,
  updateCallQualityState: state.campaign.updateCallQualityState,
  refreshCallItemState: state.campaign.refreshCallItemState,
  reprocessCallState: state.campaign.reprocessCallState,
  bulkReprocessCallsState: state.campaign.bulkReprocessCallsState,
  toggleCallStarState: state.campaign.toggleCallStarState,
});

const mapDispatchToProps = (dispatch) => ({
  getCampaignCalls: (campaignId, params) => dispatch(getCampaignCallsApi(campaignId, params)),
  getCampaignCallsReset: () => dispatch(getCampaignCallsReset()),
  processCampaignTranscripts: (campaignId) => dispatch(processCampaignTranscriptsApi(campaignId)),
  processCampaignTranscriptsReset: () => dispatch(processCampaignTranscriptsReset()),
  retryFailedTranscripts: (campaignId) => dispatch(retryFailedTranscriptsApi(campaignId)),
  retryFailedTranscriptsReset: () => dispatch(retryFailedTranscriptsReset()),
  updateCallQuality: (campaignId, callId, quality) => dispatch(updateCallQualityApi(campaignId, callId, quality)),
  updateCallQualityReset: () => dispatch(updateCallQualityReset()),
  refreshCallItem: (campaignId, callId, type) => dispatch(refreshCallItemApi(campaignId, callId, type)),
  refreshCallItemReset: () => dispatch(refreshCallItemReset()),
  reprocessCall: (campaignId, callId) => dispatch(reprocessCallApi(campaignId, callId)),
  reprocessCallReset: () => dispatch(reprocessCallReset()),
  bulkReprocessCalls: (campaignId) => dispatch(bulkReprocessCallsApi(campaignId)),
  bulkReprocessCallsReset: () => dispatch(bulkReprocessCallsReset()),
  toggleCallStar: (campaignId, callId) => dispatch(toggleCallStarApi(campaignId, callId)),
  toggleCallStarReset: () => dispatch(toggleCallStarReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignCallsSection);
